import React, { useEffect, useState } from 'react';
import { bool, string } from 'prop-types';
import { AppStore, GooglePlay } from '@alltrails/shared-components';
import styles from './styles/styles.module.scss';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import { ENTER } from '../../utils/constants/key_codes';

const {
  strings: {
    MOBILE: { TEXT_LINK, ERROR_SEND_TEXT, SENT, SENDING },
    NAVIGATOR_MODAL: { DOWNLOAD_THE_APP, NAVIGATOR_IS_ONLY, ANY_NUMBER, APP_ONLY_FEATURE, HEAD_TO_THE }
  }
} = StringConstantsMixin;

const statuses = {
  INITIAL: 0,
  SENDING: 1,
  SENT: 2
};

const NavigatorModal = ({ languageRegionCode, branchKey, mobileBrowser }) => {
  const [ready, toggleReady] = useState(false);
  const [visible, toggleVisible] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [status, setStatus] = useState(statuses.INITIAL);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (branchKey && window.branch) {
      try {
        window.branch.init(branchKey, branchError => {
          if (!branchError) {
            toggleReady(true);
          }
        });
      } catch (branchError) {
        console.error('Error initializing branch', branchError);
      }
    }
  }, [branchKey]);

  const sendSMS = () => {
    const toNumber = phoneNumber.trim();

    if (toNumber.length === 0) {
      return;
    }

    setStatus(statuses.SENDING);

    window.branch.sendSMS(toNumber, {}, {}, sendError => {
      if (sendError) {
        setError(ERROR_SEND_TEXT);
        setStatus(statuses.INITIAL);
      } else {
        setStatus(statuses.SENT);
      }
    });
  };

  const handleKeyUp = e => {
    e.preventDefault();
    setStatus(statuses.INITIAL);
    setError(null);

    if (e.keyCode === ENTER) {
      sendSMS();
    }
  };

  if (!visible || !ready) {
    return null;
  }

  const buttonDisabled = [statuses.SENDING, statuses.SENT].includes(status);

  const buttonText = {
    [statuses.INITIAL]: TEXT_LINK,
    [statuses.SENDING]: SENDING,
    [statuses.SENT]: SENT
  }[status];

  const renderEnglish = () => {
    return (
      <>
        <div className={styles.topContainer}>
          <h2 className={styles.title}>{DOWNLOAD_THE_APP}</h2>
          {!error && <p className={styles.instructions}>{NAVIGATOR_IS_ONLY}</p>}
          {error && <p className={styles.error}>{ERROR_SEND_TEXT}</p>}
          <div className={styles.form}>
            <input
              className={styles.input}
              type="tel"
              value={phoneNumber}
              onChange={event => setPhoneNumber(event.target.value)}
              onKeyUp={handleKeyUp}
              placeholder="### – ### – ####"
            />
            <Button disabled={buttonDisabled} variant="primary" onClick={sendSMS} classes={styles.button}>
              {buttonText}
            </Button>
          </div>
        </div>
        <div className={styles.bottomContainer}>
          <p className={styles.theFinePrint}>{ANY_NUMBER}</p>
        </div>
      </>
    );
  };

  const renderNonEnglish = () => {
    const appStoreWidth = mobileBrowser ? 120 : 133;
    const googlePlayWidth = mobileBrowser ? 128 : 150;
    const instructionsClassName = mobileBrowser ? styles.instructionsNonEnglish : styles.instructions;

    return (
      <div className={styles.topContainer}>
        <h2 className={styles.titleNonEnglish}>{APP_ONLY_FEATURE}</h2>
        <p className={instructionsClassName}>{HEAD_TO_THE}</p>
        <div className={styles.storeBadges}>
          <AppStore width={appStoreWidth} languageRegionCode={languageRegionCode} className={styles.appStore} />
          <GooglePlay width={googlePlayWidth} languageRegionCode={languageRegionCode} />
        </div>
      </div>
    );
  };

  const content = languageRegionCode === 'en-US' ? renderEnglish() : renderNonEnglish();

  const containerClassNames = [styles.container];

  if (languageRegionCode !== 'en-US') {
    containerClassNames.push(styles.containerNonEnglish);
  }

  return (
    <Modal handleBackgroundClick={() => toggleVisible(false)}>
      <div className={containerClassNames.join(' ')}>
        <button type="button" className={styles.close} onClick={() => toggleVisible(false)}>
          <img className={styles.closeImage} src="https://cdn-assets.alltrails.com/assets/images/navigator-modal/close.svg" alt="close" />
        </button>
        {content}
      </div>
    </Modal>
  );
};

NavigatorModal.propTypes = {
  languageRegionCode: string.isRequired,
  mobileBrowser: bool.isRequired,
  branchKey: string.isRequired
};

export default NavigatorModal;
